import React from 'react';
import { useNavigate } from 'react-router-dom';
import boardImage from '../assets/manual/desktop-liveview.png';

// Why a club would want this at all — argued from the DESKTOP edition, because that is the product
// at its full extent. The feature list further down answers "what can it do"; this answers "what do
// we get out of it", which is the question a committee actually asks.
//
// The closing line hands over to the editions block at the bottom of the page on purpose: say what
// the tool is worth first, then which version you need.

const translations = {
  de: {
    tag: 'Was ihr davon habt',
    title: 'Die Halle erlebt das Spiel mit',
    lead: 'Ein Einlaufvideo auf der Leinwand, Musik in jeder Unterbrechung, und beim Tor der Jubel mit eurem Wappen und dem Namen der Torschützin. Für die Zuschauer wird aus einem Spiel ein Abend. Am Pult bleiben drei Handgriffe: Zeit, Tore, Strafen — der Rest läuft von selbst.',
    benefits: [
      {
        n: '01',
        title: 'Der Moment nach dem Tor',
        text: 'Die Torhymne startet, auf der Leinwand dreht euer Wappen, dann erscheint die Nummer der Torschützin und ihr Name. Zwei Sekunden, in denen die Halle laut wird — und niemand musste dafür etwas drücken.',
      },
      {
        n: '02',
        title: 'Stimmung schon vor dem Anpfiff',
        text: 'Während sich die Halle füllt, läuft eure Playlist. Kurz vor dem Einlaufen das Intro-Video, dazwischen die Clips eurer Sponsoren. Die Zuschauer sitzen nicht in einer stillen Halle und warten.',
      },
      {
        n: '03',
        title: 'Am Pult bleibt es ruhig',
        text: 'Am Spieltag sitzt selten die eingespielte Fachkraft am Pult, sondern wer gerade Zeit hat. Teams, Kader, Musik und Videos sind längst hinterlegt: Teams auswählen, Start drücken — der Rest passiert von selbst.',
      },
      {
        n: '04',
        title: 'Der Verein sieht aus wie ein Verein',
        text: 'Wappen, Vereinsfarbe, Sponsorenlogos — auf dem grossen Bildschirm, im Livestream und auf jedem Foto, das davon gemacht wird. Auch der Gastverein erscheint mit seinem Wappen.',
      },
    ],
    calloutTitle: 'Und wenn etwas dazwischenkommt',
    calloutText: 'Kein Netz in der Halle, ein Rechner, den niemand kennt, fünf Minuten vor dem Anpfiff: Die Desktop-Edition läuft offline, und die ganze Einrichtung lässt sich als eine Datei auf einen anderen Rechner mitnehmen.',
    bridge: 'So weit die Desktop-Edition. Es gibt sie in drei Ausbaustufen — bis hin zu einer, die dauerhaft nichts kostet. Der Vergleich steht unten auf dieser Seite.',
    cta: 'Desktop-Edition ansehen',
    imageCaption: 'Was die Zuschauer sehen: Spielzeit, Stand, Strafen — in der Farbe des Vereins.',
  },
  en: {
    tag: 'What you get',
    title: 'The hall lives the match with you',
    lead: 'An entrance video on the big screen, music at every stoppage, and on a goal the celebration with your crest and the scorer’s name. For the audience a match turns into an evening. At the desk three things are left: clock, goals, penalties — the rest runs itself.',
    benefits: [
      {
        n: '01',
        title: 'The moment after the goal',
        text: 'The anthem starts, your crest spins across the screen, then the scorer’s number and her name appear. Two seconds in which the hall gets loud — and nobody had to press anything for it.',
      },
      {
        n: '02',
        title: 'Atmosphere before the first whistle',
        text: 'Your playlist plays while the hall fills up. The intro video just before the teams come out, your sponsors’ clips in between. The audience is not sitting in a silent hall, waiting.',
      },
      {
        n: '03',
        title: 'The desk stays calm',
        text: 'On match day the desk is rarely staffed by a practised specialist, but by whoever has the time. Teams, rosters, music and video are long since set up: pick the teams, press start — the rest happens by itself.',
      },
      {
        n: '04',
        title: 'The club looks like a club',
        text: 'Crest, club colour, sponsor logos — on the big screen, in the livestream and in every photo taken of it. The visiting club appears with their crest too.',
      },
    ],
    calloutTitle: 'And when something gets in the way',
    calloutText: 'No network in the hall, a computer nobody knows, five minutes before the whistle: the desktop edition runs offline, and the whole setup travels to another machine as a single file.',
    bridge: 'That is the desktop edition. It comes in three levels — including one that costs nothing, permanently. The comparison is at the bottom of this page.',
    cta: 'See the desktop edition',
    imageCaption: 'What the audience sees: clock, score, penalties — in the club’s colour.',
  },
};

export default function BenefitSection({ lang = 'de' }) {
  const t = translations[lang] || translations.de;
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center px-8 py-28 w-full bg-white max-md:px-5 max-md:py-20">
      <div className="flex flex-col w-full max-w-[1200px]">
        <div className="mb-14 max-w-[820px]">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-blue-600">{t.tag}</div>
          <h2 id="benefit-heading" className="text-6xl font-extrabold leading-tight text-stone-900 max-md:text-4xl">
            {t.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-stone-600">{t.lead}</p>
        </div>

        <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
          {t.benefits.map((b) => (
            <div key={b.n}>
              <div className="text-sm font-bold text-blue-600">{b.n}</div>
              <h3 className="mt-2 text-2xl font-bold text-stone-900">{b.title}</h3>
              <p className="mt-2 text-lg leading-relaxed text-stone-600">{b.text}</p>
            </div>
          ))}
        </div>

        <figure className="mt-16">
          <img
            src={boardImage}
            alt={t.imageCaption}
            loading="lazy"
            className="w-full rounded-2xl border border-stone-200 shadow-lg"
          />
          <figcaption className="mt-3 text-sm text-stone-500">{t.imageCaption}</figcaption>
        </figure>

        {/* The objection a club raises before the price: what if the evening goes sideways. */}
        <div className="mt-16 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-10 text-white md:p-14">
          <h3 className="text-2xl font-bold md:text-3xl">{t.calloutTitle}</h3>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-300">{t.calloutText}</p>
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6">
          <p className="max-w-2xl text-lg text-stone-600">{t.bridge}</p>
          <button
            type="button"
            onClick={() => navigate('/desktop')}
            className="rounded-lg bg-blue-600 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            {t.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
